import React, { useCallback, useEffect, useRef } from 'react'
import {
  selectCrossClientCurrentTabLoaded,
  selectCrossClientHasActions,
  selectCrossClientPaging,
  selectCrossClientResultsPollingEnabled,
  selectCrossClientResultsExecution,
  selectCrossClientActiveEvaluations,
  selectCrossClientSearch,
  selectCrossClientSelectedTab,
  selectShouldPollCrossClientExecutionResults,
  canLocalEvaluateForTab,
  invalidateInactiveCrossClientResultTabs,
  setCrossClientMatchDistribution,
  setCrossClientPageResults,
  setCrossClientProgressStatuses,
  setCrossClientResultBuckets,
  setCrossClientResultsExecution,
  mergeCrossClientActiveEvaluations,
  startCrossClientExecutionPolling,
  stopCrossClientExecutionPolling,
  tabToResultFilter,
} from '../../../store/crossClient/crossClientResults'
import { usePollingEffect } from '../../../apps/professional-app/fa/common/hooks/usePollingEffect'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import {
  useLazyGetCrossClientEvaluationsQuery,
  useLazyGetCrossClientMatchDistributionQuery,
  useLazyGetCrossClientQueryExecutionQuery,
  useLazyGetCrossClientQueryExecutionResultsQuery,
  useLazyGetCrossClientResultBucketsQuery,
} from '../../../store/crossClient/crossClient'
import { CrossClientQueryEvaluationStatuses, CrossClientQueryResultOrderBys } from '../../../models/CrossClient'

const RESULTS_POLL_INTERVAL_MS = 4000

export interface CrossClientResultsPollerProps {
  executionId: string
}

export const CrossClientResultsPoller: React.FC<CrossClientResultsPollerProps> = ({ executionId }) => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const shouldPoll = useAppSelector(selectShouldPollCrossClientExecutionResults)
  const pollingEnabled = useAppSelector(selectCrossClientResultsPollingEnabled)
  const activeEvaluations = useAppSelector(selectCrossClientActiveEvaluations)
  const execution = useAppSelector(selectCrossClientResultsExecution)
  const selectedTab = useAppSelector(selectCrossClientSelectedTab)
  const paging = useAppSelector(selectCrossClientPaging)
  const search = useAppSelector(selectCrossClientSearch)
  const hasActions = useAppSelector(selectCrossClientHasActions)
  const tabLoaded = useAppSelector(selectCrossClientCurrentTabLoaded)

  const [triggerGetCrossClientQueryExecution] = useLazyGetCrossClientQueryExecutionQuery()
  const [triggerGetResults] = useLazyGetCrossClientQueryExecutionResultsQuery()
  const [triggerGetMatchDistribution] = useLazyGetCrossClientMatchDistributionQuery()
  const [triggerGetResultBuckets] = useLazyGetCrossClientResultBucketsQuery()
  const [triggerGetEvaluations] = useLazyGetCrossClientEvaluationsQuery()

  const lastUpdatedAtRef = useRef<string | null>(null)
  const activeCountRef = useRef<number | null>(null)

  const hasActive = activeEvaluations.length > 0

  useEffect(() => {
    if (shouldPoll || hasActive) {
      dispatch(startCrossClientExecutionPolling())
    } else {
      dispatch(stopCrossClientExecutionPolling())
    }
  }, [dispatch, shouldPoll, hasActive])

  const orderBy = canLocalEvaluateForTab(selectedTab, execution) ? CrossClientQueryResultOrderBys.NEXT_STEP : undefined

  const refreshPage = useCallback(async () => {
    const tab = selectedTab
    const pageIndex = Math.max(paging.page - 1, 0)
    const { match, status } = tabToResultFilter(tab)
    const response = await triggerGetResults({
      accountId,
      executionId,
      match,
      status,
      search: search.clientName ?? undefined,
      hasActions: hasActions ?? undefined,
      orderBy,
      include: ['bulkResolverAnswers', 'localResolverAnswers'],
      page: pageIndex,
      size: paging.size,
    }).unwrap()
    dispatch(setCrossClientPageResults({ tab, response }))
  }, [dispatch, accountId, executionId, selectedTab, paging.page, paging.size, search.clientName, hasActions, orderBy, triggerGetResults])

  const refreshAuxiliary = useCallback(async (): Promise<{ activeCount: number; lastUpdatedAt: string | null }> => {
    const [execution, distribution, buckets, evaluations] = await Promise.all([
      triggerGetCrossClientQueryExecution({ accountId, executionId, expand: ['progress', 'evaluationCounts'] }).unwrap(),
      triggerGetMatchDistribution({ accountId, executionId }).unwrap(),
      triggerGetResultBuckets({ accountId, executionId }).unwrap(),
      triggerGetEvaluations({
        accountId,
        executionId,
        statuses: [
          CrossClientQueryEvaluationStatuses.PENDING,
          CrossClientQueryEvaluationStatuses.QUEUED,
          CrossClientQueryEvaluationStatuses.RUNNING,
          CrossClientQueryEvaluationStatuses.CANCELING,
        ],
        expand: ['progress'],
      }).unwrap(),
    ])
    if (!execution) {
      dispatch(stopCrossClientExecutionPolling())
      return { activeCount: 0, lastUpdatedAt: null }
    }
    dispatch(setCrossClientResultsExecution(execution))
    dispatch(setCrossClientProgressStatuses(execution.progress?.statuses ?? {}))
    if (distribution) {
      dispatch(setCrossClientMatchDistribution(distribution))
    }
    dispatch(setCrossClientResultBuckets(buckets ?? []))
    const active = evaluations?.data ?? []
    dispatch(mergeCrossClientActiveEvaluations(active))
    return { activeCount: active.length, lastUpdatedAt: execution.progress?.lastUpdatedAt ?? null }
  }, [dispatch, accountId, executionId, triggerGetCrossClientQueryExecution, triggerGetMatchDistribution, triggerGetResultBuckets, triggerGetEvaluations])

  useEffect(() => {
    refreshAuxiliary()
      .then(({ activeCount, lastUpdatedAt }) => {
        activeCountRef.current = activeCount
        lastUpdatedAtRef.current = lastUpdatedAt
      })
      .catch(console.error)
  }, [refreshAuxiliary])

  useEffect(() => {
    if (!tabLoaded && execution) {
      refreshPage().catch(console.error)
    }
  }, [tabLoaded, execution, refreshPage])

  const pollTick = useCallback(async () => {
    const { activeCount, lastUpdatedAt } = await refreshAuxiliary()
    const previousActive = activeCountRef.current
    const activeShrank = previousActive !== null && activeCount < previousActive
    const progressChanged = lastUpdatedAt !== lastUpdatedAtRef.current
    activeCountRef.current = activeCount
    lastUpdatedAtRef.current = lastUpdatedAt
    if (activeShrank || progressChanged) {
      dispatch(invalidateInactiveCrossClientResultTabs())
      await refreshPage()
    }
  }, [dispatch, refreshAuxiliary, refreshPage])

  const handlePollingStopped = useCallback(async () => {
    dispatch(stopCrossClientExecutionPolling())
  }, [dispatch])

  const { start: startResultsPolling, stop: stopResultsPolling } = usePollingEffect(pollTick, [], {
    interval: RESULTS_POLL_INTERVAL_MS,
    leading: false,
    onCleanUp: handlePollingStopped,
  })

  useEffect(() => {
    if (pollingEnabled) {
      startResultsPolling()
    } else {
      stopResultsPolling()
    }
  }, [pollingEnabled, startResultsPolling, stopResultsPolling])

  return null
}
